import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faUser, 
  faCalendarAlt, 
  faTag, 
  faHeart,
  faCheckCircle,
  faExclamationCircle,
  faSpinner
} from '@fortawesome/free-solid-svg-icons';
import { formatCurrency } from '../../utils/currencyUtils';
import './DonationTable.css';

const DonationTable = ({ donations, onLoadMore, hasMore, loading }) => {
  const [expandedDedications, setExpandedDedications] = useState(new Set());

  // Format amount with currency (currency now comes from each donation object)
  const formatAmount = (amountCents, currencyCode = 'usd') => {
    return formatCurrency(amountCents, currencyCode);
  };

  // Format date
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Format time
  const formatTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Get status info
  const getStatusInfo = (status) => {
    switch (status) {
      case 'succeeded':
        return {
          icon: faCheckCircle,
          color: '#10b981',
          label: 'Completed',
          bgColor: '#ecfdf5'
        };
      case 'pending':
        return {
          icon: faExclamationCircle,
          color: '#f59e0b',
          label: 'Pending',
          bgColor: '#fffbeb'
        };
      case 'failed':
        return {
          icon: faExclamationCircle,
          color: '#ef4444',
          label: 'Failed',
          bgColor: '#fef2f2'
        };
      case 'refunded':
        return {
          icon: faExclamationCircle,
          color: '#64748b',
          label: 'Refunded',
          bgColor: '#f8fafc'
        };
      default:
        return {
          icon: faSpinner,
          color: '#64748b',
          label: status,
          bgColor: '#f8fafc'
        };
    }
  };



  // Render status badge
  const renderStatusBadge = (donation) => {
    const statusInfo = getStatusInfo(donation.status);
    return (
      <div 
        className="status-badge" 
        style={{ 
          color: statusInfo.color,
          backgroundColor: statusInfo.bgColor
        }}
      >
        <FontAwesomeIcon 
          icon={statusInfo.icon}
        />
        <span>{statusInfo.label}</span>
      </div>
    );
  };

  // Render donor info
  const renderDonorInfo = (donation) => {
    // Check for user data in native_users (new format)
    const user = donation.native_users || donation.users; // Support both formats for backward compatibility
    
    if (!user) {
      return (
        <div className="donor-info">
          <div className="donor-name">Anonymous</div>
          <div className="donor-email text-muted">No email</div>
        </div>
      );
    }

    // Get the user's name - prefer full_name, fallback to constructing from first/last, then fallback to legacy name field
    const userName = user.full_name || 
      (user.first_name && user.last_name ? `${user.first_name} ${user.last_name}` : '') ||
      user.name || 
      'Anonymous';

    return (
      <div className="donor-info">
        <div className="donor-name">
          {userName}
        </div>
        {user.email && (
          <div className="donor-email text-muted">
            {user.email}
          </div>
        )}
      </div>
    );
  };

  // Render product type
  const renderProductType = (donation) => {
    // Try multiple possible data structures for the joined product type
    let productType = null;
    
    // Option 1: Direct nested object (Supabase JOIN format)
    if (donation.donation_product_types && donation.donation_product_types.name) {
      productType = donation.donation_product_types;
    }
    // Option 2: Array format (sometimes Supabase returns arrays for JOINs)
    else if (Array.isArray(donation.donation_product_types) && donation.donation_product_types.length > 0) {
      productType = donation.donation_product_types[0];
    }
    // Option 3: Flattened format (if JOIN was flattened)
    else if (donation.product_type_name || donation.donation_product_type_name) {
      productType = {
        name: donation.product_type_name || donation.donation_product_type_name,
        emoji: donation.product_type_emoji || donation.donation_product_type_emoji
      };
    }

    // If we found product type data, render it
    if (productType && productType.name) {
      return (
        <div className="product-type">
          {productType.emoji && (
            <span className="product-type-emoji">
              {productType.emoji}
            </span>
          )}
          <span>{productType.name}</span>
        </div>
      );
    }

    // Fallback: Show "General Donation"
    return <span className="text-muted">General Donation</span>;
  };

  // Render dedication message with expandable functionality
  const renderDedicationMessage = (donation) => {
    if (!donation.dedication_message || donation.dedication_message.trim() === '') {
      return <span className="text-muted">—</span>;
    }

    const message = donation.dedication_message;
    const maxLength = 60; // Show more characters before truncating
    const donationId = donation.id;
    const isExpanded = expandedDedications.has(donationId);
    
    // If message is short enough, show it in full
    if (message.length <= maxLength) {
      return <span className="dedication-message">{message}</span>;
    }

    // For long messages, provide expand/collapse functionality
    const toggleExpanded = (e) => {
      e.stopPropagation(); // Prevent row click events
      const newExpanded = new Set(expandedDedications);
      if (isExpanded) {
        newExpanded.delete(donationId);
      } else {
        newExpanded.add(donationId);
      }
      setExpandedDedications(newExpanded);
    };

    return (
      <div className="dedication-message-container">
        <span className="dedication-message">
          {isExpanded ? message : `${message.substring(0, maxLength)}...`}
        </span>
        <button 
          className="dedication-toggle"
          onClick={toggleExpanded}
          type="button"
        >
          {isExpanded ? 'see less' : 'see more'}
        </button>
      </div>
    );
  };



  return (
    <div className="donation-table-container">
      <div className="table-wrapper">
        <table className="donation-table">
          <thead>
            <tr>
              <th className="col-amount">Amount</th>
              <th className="col-donor">Donor</th>
              <th className="col-type">Type</th>
              <th className="col-app-fee">Application Fee</th>
              <th className="col-dedication">Dedication</th>
              <th className="col-status">Status</th>
              <th className="col-date">Date</th>
              <th className="col-confirmation">Confirmation #</th>
            </tr>
          </thead>
          <tbody>
            {!donations || donations.length === 0 ? (
              <tr className="empty-state-row">
                <td colSpan="8" className="empty-state-cell">
                  No donations have been made yet.
                </td>
              </tr>
            ) : (
              donations.map((donation) => (
                <tr key={donation.id} className={`donation-row ${donation.status}`}>
                  <td className="col-amount">
                    <div className="amount-cell">
                      {formatAmount(donation.amount_cents, donation.currency)}
                    </div>
                  </td>
                  
                  <td className="col-donor">
                    {renderDonorInfo(donation)}
                  </td>
                  
                  <td className="col-type">
                    {renderProductType(donation)}
                  </td>
                  
                  <td className="col-app-fee">
                    <div className="app-fee-cell">
                      {donation.application_fee_covered ? (
                        <div className="app-fee-info">
                          <div className="app-fee-amount">
                            {formatAmount(donation.application_fee_cents || 0, donation.currency)}
                          </div>
                          <div className="app-fee-label text-muted">
                            Covered by donor
                          </div>
                        </div>
                      ) : (
                        <span className="text-muted">Not covered</span>
                      )}
                    </div>
                  </td>
                  
                  <td className="col-dedication">
                    {renderDedicationMessage(donation)}
                  </td>
                  
                  <td className="col-status">
                    {renderStatusBadge(donation)}
                  </td>
                  
                  <td className="col-date">
                    <div className="date-cell">
                      <div className="date-primary">{formatDate(donation.created_at)}</div>
                      <div className="date-secondary text-muted">{formatTime(donation.created_at)}</div>
                    </div>
                  </td>
                  
                  <td className="col-confirmation">
                    <div className="confirmation-cell">
                      {donation.confirmation_number || '—'}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Load more button */}
      {hasMore && (
        <div className="table-footer">
          <button 
            className="load-more-button"
            onClick={onLoadMore}
            disabled={loading}
          >
            {loading ? (
              <>
                <FontAwesomeIcon icon={faSpinner} className="fa-spin" />
                Loading...
              </>
            ) : (
              'Load More'
            )}
          </button>
        </div>
      )}
    </div>
  );
};

export default DonationTable;